/* @flow */
import * as React from 'react';
import { connect } from 'react-redux';

import { bindActionCreators } from 'redux';
import { orgTypeString as ORG_TYPE_STRING } from 'constants/SelfServeConstants';
import {
  saveLocalQuoteField,
  setPaymentMethod,
  handleCardValidityChange,
  STUDENT_COUNT_FOR_TWO_DAY_FEATURES,
  HIGH_TOUCH_STUDENT_COUNT,
  NO_TOUCH_STUDENT_COUNT_MAX,
} from 'admin/reducers/adminUpgradePage';
import { showToast } from 'reducers/toasts';
import assertions from 'lib/assertions';
import Columns2 from 'components/Columns2';
import Config from 'Config';
import EventTracking from 'lib/EventTracking';
import Field from 'components/Field';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import MailTo from 'components/MailTo';
import Input from 'components/Input';
import InputValueConstants from 'constants/InputValueConstants';
import Label from 'components/Label';
import LinkConstants from 'constants/LinkConstants';
import PhoneNumberMaskedInput from 'components/PhoneNumberMaskedInput';
import Row from 'components/Row';
import Tabs from 'components/Tabs';
import TabPanel from 'components/TabPanel';
import Text from 'components/Text';
import Time from 'components/Time';
import AdminUpgradeQuoteTotalBox from 'admin/components/AdminUpgradeQuoteTotalBox';
import AdminUpgradeShareQuoteBanner from 'admin/components/AdminUpgradeShareQuoteBanner';
import CreditCardInput from 'components/CreditCardInput';

import type { Fields } from 'admin/reducers/adminUpgradePage';
import './styles/AdminUpgradePaymentForm.scss';

type Props = {
  quote: *,
  fields: Fields,
  saveLocalQuoteField: (name: string, value: *) => void,
  showToast: (text: string, kind: 'success' | 'error') => void,
  orgTypeString: string,
  setPaymentMethod: Function,
  handleCardValidityChange: Function,
  paymentMethod: string,
  cardValid: ?boolean,
  cardState: {
    inputValid: boolean,
    errorMessage: ?string,
  },
};

class AdminUpgradePaymentForm extends React.Component<Props> {
  static trackingPrefix = 'admin_self_serve.payment_form';

  componentDidMount() {
    this.track('view');

    if (this.props.quote.studentsCount > HIGH_TOUCH_STUDENT_COUNT) {
      this.props.setPaymentMethod('invoice');
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.cardValid !== nextProps.cardState.inputValid) {
      this.props.handleCardValidityChange(nextProps.cardState.inputValid);
    }
  }

  componentWillUnmount() {
    // :sadface:
    //
    // the form pagination rely on the fact that we validate all
    // input like the backend. If an incorrect billing phone number
    // is saved in the store and the back button is pressed, the user
    // will not be able to to go the next page because the form will
    // error durring submission, and will not be able to edit the value
    // since the field isn't in the view!
    //
    // TODO validate the billing phone number area code like API does.
    this.props.saveLocalQuoteField('billingPhoneNumber', undefined);
  }

  track = (key: string) => {
    EventTracking.trackKey(`${AdminUpgradePaymentForm.trackingPrefix}.${key}`);
  };

  renderCardForm() {
    const { quote, cardState } = this.props;
    const subject = `Tax Exemption for ${quote.organization.name}`;
    return (
      <div>
        {quote.taxExempt && (
          <Text element="div" size="medium" className="AdminUpgradePaymentForm-featuresNotice">
            Email your tax exemption form to{' '}
            {
              <MailTo subject={subject} to={LinkConstants.BILLING_EMAIL}>
                {LinkConstants.BILLING_EMAIL}
              </MailTo>
            }{' '}
            with your name and your school/district name.
          </Text>
        )}
        <Field error={cardState.errorMessage}>
          <Label>Credit or debit card</Label>
          <CreditCardInput apiKey={Config.Environment.SAAS_STRIPE_KEY} />
        </Field>
      </div>
    );
  }

  renderInvoiceForm() {
    const { quote } = this.props;
    const noticeText =
      quote.studentsCount < STUDENT_COUNT_FOR_TWO_DAY_FEATURES
        ? `When paying by invoice, Remind plan features will be enabled once payment has been received.`
        : `Remind plan features will be enabled within 2 business days. Payment for the invoice is requested within 30 days.`;

    return (
      <div>
        <Text element="div" size="medium" className="AdminUpgradePaymentForm-featuresNotice">
          {noticeText}
        </Text>
        <FormElement
          label="Purchase order number"
          validate={e => {
            assertions.lengthLesserThanOrEqualTo(e.target.value, 80);
          }}
          onValidChange={e => this.props.saveLocalQuoteField('purchaseOrderNumber', e.target.value)}
          onInvalidChange={() => this.props.saveLocalQuoteField('purchaseOrderNumber', undefined)}
          element={Input}
          elementProps={{
            placeholder: 'Optional',
            defaultValue: this.props.fields.get('purchaseOrderNumber'),
            onFocus: () => this.track('purchase_order.click'),
          }}
        />
      </div>
    );
  }

  renderPaymentTabs() {
    return (
      <div className="AdminUpgradePaymentForm-paymentOptions">
        <Tabs tabAlign="center">
          <TabPanel
            onClick={() => this.props.setPaymentMethod('card')}
            title={
              <Heading
                level="2"
                levelStyle="6"
                className="AdminUpgradePaymentForm-paymentOptions-cardTab">
                Pay by card
              </Heading>
            }>
            {this.renderCardForm()}
          </TabPanel>
          <TabPanel
            onClick={() => this.props.setPaymentMethod('invoice')}
            title={
              <Heading
                level="2"
                levelStyle="6"
                className="AdminUpgradePaymentForm-paymentOptions-invoiceTab">
                Pay by invoice
              </Heading>
            }>
            {this.renderInvoiceForm()}
          </TabPanel>
        </Tabs>
      </div>
    );
  }

  render() {
    const { quote } = this.props;
    const highTouch = quote.studentsCount > HIGH_TOUCH_STUDENT_COUNT;
    const noTouch = quote.studentsCount <= NO_TOUCH_STUDENT_COUNT_MAX;

    return (
      <div className="AdminUpgradePaymentForm">
        <Row>
          <div className="AdminUpgradePaymentForm-reviewBox">
            <div className="AdminUpgradePaymentForm-reviewBox-school">
              <div>
                <Text element="div" size="medium" weight="semibold">
                  Remind plan for 1 year
                </Text>
                <Text element="div" color="thunder">
                  {quote.organization.name}
                </Text>
                <Text element="div" color="thunder">
                  <Time
                    date={new Date(quote.startDate)}
                    month="long"
                    day="numeric"
                    year="numeric"
                  />{' '}
                  to{' '}
                  <Time date={new Date(quote.endDate)} month="long" day="numeric" year="numeric" />
                </Text>
              </div>
            </div>
          </div>
          <AdminUpgradeQuoteTotalBox quote={quote} />
        </Row>
        <Row>
          <AdminUpgradeShareQuoteBanner
            shareToken={quote.shareToken}
            showToast={this.props.showToast}
            orgName={quote.organization.name}
          />
        </Row>
        <Row>
          {/* eslint-disable-next-line no-nested-ternary */}
          {highTouch
            ? this.renderInvoiceForm()
            : noTouch
            ? this.renderCardForm()
            : this.renderPaymentTabs()}
        </Row>
        <div>
          <Row size="small">
            <Heading level="2" levelStyle="6" divided>
              Billing contact details
            </Heading>
          </Row>
          <Columns2>
            <FormElement
              label="First name"
              validate={e => {
                assertions.lengthLesserThanOrEqualTo(
                  e.target.value,
                  InputValueConstants.FIRST_NAME_LENGTH
                );
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e =>
                this.props.saveLocalQuoteField('billingFirstName', e.target.value)
              }
              onInvalidChange={() => this.props.saveLocalQuoteField('billingFirstName', undefined)}
              element={Input}
              elementProps={{
                defaultValue: this.props.fields.get('billingFirstName'),
                onFocus: () => this.track('billing_first_name.click'),
              }}
            />
            <FormElement
              label="Last name"
              validate={e => {
                assertions.lengthLesserThanOrEqualTo(
                  e.target.value,
                  InputValueConstants.LAST_NAME_LENGTH
                );
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.props.saveLocalQuoteField('billingLastName', e.target.value)}
              onInvalidChange={() => this.props.saveLocalQuoteField('billingLastName', undefined)}
              element={Input}
              elementProps={{
                defaultValue: this.props.fields.get('billingLastName'),
                onFocus: () => this.track('billing_last_name.click'),
              }}
            />
          </Columns2>
          <Columns2>
            <FormElement
              label="Email address"
              validate={e => {
                assertions.email(e.target.value);
                assertions.notEmpty(e.target.value);
              }}
              onValidChange={e => this.props.saveLocalQuoteField('billingEmail', e.target.value)}
              onInvalidChange={() => this.props.saveLocalQuoteField('billingEmail', undefined)}
              element={Input}
              elementProps={{
                defaultValue: this.props.fields.get('billingEmail'),
                onFocus: () => this.track('billing_email.click'),
              }}
            />
            <FormElement
              label="Phone number"
              validate={e => {
                assertions.number(e.raw, 'Invalid phone number.');
                assertions.completePhoneNumber(e.raw, 'Invalid phone number.');
              }}
              onValidChange={e => this.props.saveLocalQuoteField('billingPhoneNumber', e.raw)}
              onInvalidChange={() =>
                this.props.saveLocalQuoteField('billingPhoneNumber', undefined)
              }
              element={PhoneNumberMaskedInput}
              elementProps={{
                defaultValue: this.props.fields.get('billingPhoneNumber'),
                onFocus: () => this.track('billing_phone_number.click'),
              }}
            />
          </Columns2>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    fields: state.adminUpgradePage.fields,
    quote: state.adminUpgradePage.quote,
    orgTypeString: ORG_TYPE_STRING[state.adminUpgradePage.fields.get('orgType')],
    paymentMethod: state.adminUpgradePage.paymentMethod,
    cardValid: state.adminUpgradePage.cardValid,
    cardState: {
      inputValid: state.creditCards.inputValid,
      errorMessage: state.creditCards.errorMessage,
    },
  };
}

function mapDispatchToProps(dispatch: Function) {
  // $FlowIssue
  return bindActionCreators(
    {
      saveLocalQuoteField,
      showToast,
      setPaymentMethod,
      handleCardValidityChange,
    },
    dispatch
  );
}

export { AdminUpgradePaymentForm };
export default connect(mapStateToProps, mapDispatchToProps)(AdminUpgradePaymentForm);
